/* Dreamey PDP video testimonials.
   Muted low-res previews auto-advance one card at a time: when the active
   preview ends, the strip scrolls right and the next card plays, looping
   back to the first. Clicking a card opens the full-resolution video with
   audio in a modal. Manually scrolling the strip (swipe, wheel, or arrows)
   redirects the chain: whichever card the strip settles on starts playing.
   Autoplay is skipped for reduced-motion and Save-Data users. */

class DreameyVideoTestimonials extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector('.dreamey-pdp-vt__track');
    this.cards = Array.from(this.querySelectorAll('.dreamey-pdp-vt__card'));
    this.modal = document.getElementById('dreamey-pdp-vt-modal');
    if (!this.track || this.cards.length === 0) return;

    this.index = 0;
    this.activeVideo = null;
    this.started = false;
    this.cancelled = false;
    this.modalOpen = false;
    this.autoScrolling = false;
    this.autoScrollTimer = null;

    this.bindCards();
    if (this.modal) this.bindModal();
    this.bindArrows();

    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const saveData = navigator.connection && navigator.connection.saveData;
    if (reducedMotion || saveData) return; // posters + click-to-play only

    // A scroll we did not initiate means the shopper took control: once the
    // strip settles, redirect the chain to whichever card is now in view.
    this.settleTimer = null;
    this.track.addEventListener('scroll', () => {
      if (this.autoScrolling) return;
      clearTimeout(this.settleTimer);
      this.settleTimer = setTimeout(() => {
        if (this.autoScrolling || this.modalOpen) return;
        this.cancelled = false; // a real gesture happened, autoplay is allowed again
        this.started = true; // the observer must not restart the chain at card 0
        const i = this.nearestIndex();
        if (i !== this.index || !this.activeVideo) this.playAt(i, { scroll: false });
      }, 180);
    }, { passive: true });

    // Start when the strip first becomes visible; pause offscreen (this also
    // covers the Details accordion being collapsed, which clips the strip).
    this.io = new IntersectionObserver((entries) => {
      if (entries[0].isIntersecting) {
        if (!this.started) {
          this.started = true;
          this.playAt(0);
        } else {
          this.resumeInline();
        }
      } else {
        this.pauseInline();
      }
    }, { threshold: 0.3 });
    this.io.observe(this);
  }

  /* Inline muted preview chain */

  nearestIndex() {
    const x = this.track.scrollLeft;
    let best = 0;
    let bestDist = Infinity;
    this.cards.forEach((card, i) => {
      const d = Math.abs(card.offsetLeft - x);
      if (d < bestDist) { bestDist = d; best = i; }
    });
    return best;
  }

  playAt(i, opts) {
    const scroll = !opts || opts.scroll !== false;
    if (this.cancelled) return;
    this.stopInline();
    this.index = i;
    const card = this.cards[i];
    const url = card.dataset.previewUrl;
    if (!url) { this.advance(); return; }

    const v = document.createElement('video');
    v.className = 'dreamey-pdp-vt__inline-video';
    v.muted = true;
    v.setAttribute('muted', '');
    v.playsInline = true;
    v.setAttribute('playsinline', '');
    v.preload = 'auto';
    v.src = url;
    v.addEventListener('ended', () => this.advance());
    v.addEventListener('error', () => this.advance());
    card.querySelector('.dreamey-pdp-vt__media').appendChild(v);
    card.classList.add('is-playing');
    this.activeVideo = v;
    if (scroll) this.scrollToCard(card);

    const attempt = v.play();
    if (attempt) attempt.catch(() => this.cancelAutoplay()); // blocked: fall back to posters
  }

  advance() {
    if (this.cancelled || this.modalOpen) return;
    this.playAt((this.index + 1) % this.cards.length);
  }

  stopInline() {
    if (this.activeVideo) {
      this.activeVideo.pause();
      this.activeVideo.remove();
      this.activeVideo = null;
    }
    this.cards.forEach((c) => c.classList.remove('is-playing'));
  }

  pauseInline() {
    if (this.activeVideo) this.activeVideo.pause();
  }

  resumeInline() {
    if (this.activeVideo && !this.modalOpen && !this.cancelled) {
      this.activeVideo.play().catch(() => {});
    }
  }

  cancelAutoplay() {
    this.cancelled = true;
    this.stopInline();
  }

  scrollToCard(card) {
    this.autoScrolling = true;
    clearTimeout(this.autoScrollTimer);
    const smooth = !window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    this.track.scrollTo({ left: card.offsetLeft, behavior: smooth ? 'smooth' : 'auto' });
    // Long enough to cover the smooth scroll (including the wrap back to card
    // 0) but short enough that a shopper's follow-up swipe is honored.
    this.autoScrollTimer = setTimeout(() => { this.autoScrolling = false; }, 900);
  }

  /* Arrows: play the previous/next card (one step at a time), scrolling it
     into view as far as the strip allows */

  bindArrows() {
    this.querySelectorAll('.dreamey-pdp-vt__arrow').forEach((btn) => {
      btn.addEventListener('click', () => {
        this.cancelled = false; // explicit gesture, playback is allowed
        this.started = true;
        const target = Math.min(Math.max(this.index + Number(btn.dataset.dir), 0), this.cards.length - 1);
        if (target !== this.index || !this.activeVideo) this.playAt(target);
      });
    });
  }

  /* Modal */

  bindCards() {
    this.cards.forEach((card) => {
      card.addEventListener('click', () => {
        const url = card.dataset.videoUrl;
        if (url && this.modal) this.openModal(url, card);
      });
    });
  }

  bindModal() {
    this.modalVideo = this.modal.querySelector('.dreamey-video-modal__video');
    this.modalClose = this.modal.querySelector('.dreamey-video-modal__close');
    this.modalClose.addEventListener('click', (e) => {
      e.stopPropagation();
      this.closeModal();
    });
    this.modal.addEventListener('click', (e) => {
      if (e.target === this.modal) this.closeModal();
    });
    document.addEventListener('keydown', (e) => {
      if (!this.modalOpen) return;
      if (e.key === 'Escape') { this.closeModal(); return; }
      if (e.key === 'Tab') {
        // Only the close button is focusable inside the modal: keep focus on it.
        e.preventDefault();
        this.modalClose.focus();
      }
    });
  }

  openModal(url, trigger) {
    this.lastFocus = trigger || document.activeElement;
    this.modalOpen = true;
    this.pauseInline();
    this.modalVideo.muted = false;
    this.modalVideo.src = url;
    this.modal.classList.add('is-active');
    this.modal.setAttribute('aria-hidden', 'false');
    document.body.style.overflow = 'hidden';
    this.modalVideo.play().catch(() => {});
    this.modalClose.focus();
  }

  closeModal() {
    this.modalVideo.pause();
    this.modalVideo.src = '';
    this.modalOpen = false;
    this.modal.classList.remove('is-active');
    this.modal.setAttribute('aria-hidden', 'true');
    document.body.style.overflow = '';
    if (this.lastFocus && typeof this.lastFocus.focus === 'function') this.lastFocus.focus();
    this.lastFocus = null;
    this.resumeInline();
  }
}

if (!customElements.get('dreamey-video-testimonials')) {
  customElements.define('dreamey-video-testimonials', DreameyVideoTestimonials);
}
